import { Deployment } from 'types/deployment';

import { addErrorNotification } from 'services/notification';
import { pluralize } from 'services/string';
import { useAPI } from 'hooks/api/useAPI';

import { useMutationWithError } from './useMutationWithError';
import { useBillingMutation } from './useBillingMutation';

export function useUpdateDeploymentsState() {
  const { applicationID, client } = useAPI();

  const { mutate: start, isLoading: isStarting } = useBillingMutation(
    (deploymentIds: Deployment['id'][]) => {
      if (!deploymentIds.length || !deploymentIds) {
        return Promise.resolve([]);
      }

      return Promise.all(
        deploymentIds.map((id) =>
          client.post(`/apps/${applicationID}/deployments/${id}/start`)
        )
      );
    },
    {
      entity: 'deployment',
      action: 'start',
      onError(error, deploymentIds) {
        addErrorNotification({
          title: `Unable to start ${pluralize(deploymentIds.length, 'deployment')}`,
          error,
        });
      },
    }
  );

  const { mutate: stop, isLoading: isStopping } = useMutationWithError(
    (deploymentIds: Deployment['id'][]) => {
      if (!deploymentIds.length || !deploymentIds) {
        return Promise.resolve([]);
      }

      return Promise.all(
        deploymentIds.map((id) =>
          client.post(`/apps/${applicationID}/deployments/${id}/stop`)
        )
      );
    },
    {
      entity: 'deployment',
      action: 'stop',
      onError(error, deploymentIds) {
        addErrorNotification({
          title: `Unable to stop ${pluralize(deploymentIds.length, 'deployment')}`,
          error,
        });
      },
    }
  );

  return { start, stop, isStarting, isStopping };
}
